import { TIPO_TURNO_LABEL } from "../../lib/turnoCockpit";

// La tarjeta de HOY: mes y año, el número del día en grande y el turno que te
// toca, con el color de su token. Es el ancla del calendario y vive en DOS
// superficies (el riel de MiPAE en turno y la tira del Home), por eso es un
// componente compartido: dos copias del mismo dibujo se desincronizan sin que
// nadie se dé cuenta (norma de coherencia 2026-07-16).
//
// Vocabulario de color: `shift-*`, el mismo de la vista mensual, del widget de
// Android y de las celdas de la semana. Un turno tiene UN color en toda la app.
// Un día libre también tiene el suyo (sage): el calendario siempre trae color,
// nunca una card en blanco.
//
// Dirección B (2026-09-10, elegida por el fundador entre tres maquetas): la
// card ya NO se tiñe entera; el color vive en el número del día y en la
// pastilla del turno. No reintroducir el gradiente por celda.
//
// Contrato honesto con `turnoHoy`:
//   null   → no sabemos (sin rotación configurada): se muestra la fecha y NADA
//            del turno. Ni siquiera "Día libre", que sería inventar.
//   libre  → "Día libre".
//   resto  → etiqueta del turno y su horario.

const TONO = { largo: "work", diurno: "day", noche: "night", 24: "night", libre: "free" };

// El número del día se tiñe con el turno: no es decoración, dice de un vistazo
// qué clase de día es. En día libre se queda en tinta (el sage apagaría la
// cifra, que es lo que se viene a leer).
const NUMERO = {
  work:  "text-[var(--shift-work-color)]",
  day:   "text-[var(--shift-day-color)]",
  night: "text-[var(--shift-night-color)]",
  free:  "text-ink",
  none:  "text-ink",
};
// Pastilla del turno (dirección B, 2026-09-10): la card ya no se tiñe entera,
// el color vive acá y en el número. `none` no se usa (sin turno no hay fila).
const PASTILLA = {
  work:  "bg-[var(--shift-work-bg)] text-[var(--shift-work-color)]",
  day:   "bg-[var(--shift-day-bg)] text-[var(--shift-day-color)]",
  night: "bg-[var(--shift-night-bg)] text-[var(--shift-night-color)]",
  free:  "bg-[var(--shift-free-bg)] text-[var(--shift-free-color)]",
  none:  "bg-surface text-ink-muted",
};

export default function TarjetaHoy({ turnoHoy = null, ahora = new Date(), className = "" }) {
  const tono = turnoHoy ? (TONO[turnoHoy.tipo] || "none") : "none";
  const conTurno = !!(turnoHoy && turnoHoy.tipo !== "libre");

  const mesAno = ahora.toLocaleDateString("es-CL", { month: "long", year: "numeric" });
  const nombreDia = ahora.toLocaleDateString("es-CL", { weekday: "long" });

  return (
    <div className={`rounded-tk-md border border-edge bg-paper p-4 ${className}`}>
      <p className="text-10px font-bold uppercase tracking-[0.14em] text-ink-faint font-sans">{mesAno}</p>
      <div className="mt-1.5 flex items-center gap-3">
        <span className={`font-serif text-46px leading-[0.9] tracking-[-0.03em] tabular-nums ${NUMERO[tono]}`}>
          {ahora.getDate()}
        </span>
        <span className="text-body font-semibold text-ink font-sans capitalize leading-tight">{nombreDia}</span>
      </div>
      {turnoHoy && (
        <div className="mt-3 pt-3 border-t border-edge/70 flex flex-wrap items-center gap-2 text-small font-sans">
          {conTurno ? (
            <>
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold font-sans ${PASTILLA[tono]}`}>
                {TIPO_TURNO_LABEL[turnoHoy.tipo] || "Turno"}
              </span>
              {turnoHoy.horaInicio && (
                <span className="text-ink-muted tabular-nums">{turnoHoy.horaInicio} a {turnoHoy.horaFin}</span>
              )}
            </>
          ) : (
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-semibold font-sans ${PASTILLA[tono]}`}>
              Día libre
            </span>
          )}
        </div>
      )}
    </div>
  );
}
